import { useCallback } from 'react';
import { SettingType } from '@kirin/api-client/contracts/settings';
import type { SeverityType } from '@kirin/api-client/contracts/shared/severity';
import type { SecretConfidence } from '@kirin/api-client/helpers/secret-confidence';
import { useOrganizationPolicies } from '@/hooks/api/useOrganizationPolicies';
import { useOrgSetting, useUpsertOrgSetting } from '@/hooks/api/useOrgSettings';
import { useToast } from '@/hooks/use-toast';
import { hasAnyEnforcePolicy as anyPolicyEnforces } from '../hasAnyEnforcePolicy';

export interface OrgPolicySettingsFlags {
  isEnforcePolicyModeEnabled: boolean;
  isSecretConfidenceThresholdEnabled: boolean;
}

export interface OrgPolicySettings {
  hasAnyEnforcePolicy: boolean;
  severity: SeverityType;
  isLoadingSeverity: boolean;
  hasSeverityError: boolean;
  isSavingSeverity: boolean;
  onSeverityChange: (severity: SeverityType) => Promise<void>;
  confidence: SecretConfidence;
  isLoadingConfidence: boolean;
  hasConfidenceError: boolean;
  isSavingConfidence: boolean;
  onConfidenceChange: (confidence: SecretConfidence) => Promise<void>;
}

/**
 * Org enforcement-severity and secret-confidence thresholds for Policies →
 * Policy Settings (KIR-5106). The caller has already checked access.
 */
export function useOrgPolicySettings(
  organizationId: string,
  {
    isEnforcePolicyModeEnabled,
    isSecretConfidenceThresholdEnabled,
  }: OrgPolicySettingsFlags
): OrgPolicySettings {
  const { toast } = useToast();

  const { data: orgPolicies } = useOrganizationPolicies(
    organizationId,
    isEnforcePolicyModeEnabled
  );
  const hasAnyEnforcePolicy = anyPolicyEnforces(orgPolicies);

  const orgEnforcementQuery = useOrgSetting(
    organizationId,
    SettingType.EnforcementSeverityThreshold,
    isEnforcePolicyModeEnabled
  );
  const upsertOrgEnforcementMutation = useUpsertOrgSetting(
    organizationId,
    SettingType.EnforcementSeverityThreshold
  );
  const severity = ((
    orgEnforcementQuery.data?.config as { severity?: SeverityType } | undefined
  )?.severity ?? 'high') as SeverityType;
  const onSeverityChange = useCallback(
    async (next: SeverityType): Promise<void> => {
      try {
        await upsertOrgEnforcementMutation.mutateAsync({
          body: { config: { severity: next } },
        });
        toast({
          title: 'Settings saved',
          description: 'Enforcement severity threshold updated successfully',
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Failed to update',
          description: 'Could not save enforcement severity threshold',
        });
      }
    },
    [upsertOrgEnforcementMutation, toast]
  );

  const orgSecretConfidenceQuery = useOrgSetting(
    organizationId,
    SettingType.SecretConfidenceThreshold,
    isSecretConfidenceThresholdEnabled
  );
  const upsertOrgSecretConfidenceMutation = useUpsertOrgSetting(
    organizationId,
    SettingType.SecretConfidenceThreshold
  );
  const confidence = ((
    orgSecretConfidenceQuery.data?.config as
      | { confidence?: SecretConfidence }
      | undefined
  )?.confidence ?? 'high') as SecretConfidence;
  const onConfidenceChange = useCallback(
    async (next: SecretConfidence): Promise<void> => {
      try {
        await upsertOrgSecretConfidenceMutation.mutateAsync({
          body: { config: { confidence: next } },
        });
        toast({
          title: 'Settings saved',
          description: 'Secret alert confidence threshold updated successfully',
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Failed to update',
          description: 'Could not save secret alert confidence threshold',
        });
      }
    },
    [upsertOrgSecretConfidenceMutation, toast]
  );

  return {
    hasAnyEnforcePolicy,
    severity,
    isLoadingSeverity: orgEnforcementQuery.isPending,
    hasSeverityError: orgEnforcementQuery.isError,
    isSavingSeverity: upsertOrgEnforcementMutation.isPending,
    onSeverityChange,
    confidence,
    isLoadingConfidence: orgSecretConfidenceQuery.isPending,
    hasConfidenceError: orgSecretConfidenceQuery.isError,
    isSavingConfidence: upsertOrgSecretConfidenceMutation.isPending,
    onConfidenceChange,
  };
}
